import React, { memo, useCallback, useState } from 'react'
import { makeStyles, useTheme } from '@material-ui/styles'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/CloseRounded'
import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import Button from '@material-ui/core/Button'
import Popper from '@material-ui/core/Popper'
import Paper from '@material-ui/core/Paper'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import { MenuItem, Theme } from '@material-ui/core'
import Divider from '@material-ui/core/Divider'
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer'
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery'
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDownOutlined'

import styles from './styles'
import { EntityLogo } from 'components'
import { EEntity } from 'models'
import { StoreValue } from 'store'

const useStyles = makeStyles(styles)

const HeaderView: React.FunctionComponent<any> = props => {
	const { activeCompany, companies, handleChoseCompanyJob, team, locations } =
		props
	const history = useHistory()
	const theme: Theme = useTheme()
	const classes = useStyles()
	const goBack = useCallback(() => {
		history.goBack()
	}, [history])
	const { t } = useTranslation()
	const [isMenuVisible, setIsMenuVisible] = React.useState(false)
	const companyMenuRef: React.Ref<any> = React.useRef(null)
	const arrowProfileRef: React.Ref<any> = React.useRef(null)
	const [openDialog, setOpenDialog] = useState(false)
	const isSmDown: boolean = useMediaQuery(theme.breakpoints.down('sm'))
	const showCompanyMenu = useCallback(() => {
		isMenuVisible ? setIsMenuVisible(false) : setIsMenuVisible(true)
	}, [isMenuVisible])
	const handleOpenDialog = useCallback(() => {
		if (locations.length > 0 || team) {
			setOpenDialog(true)
		} else {
			showCompanyMenu()
		}
	}, [team, locations, showCompanyMenu])
	const handleCloseDialog = useCallback(() => {
		setOpenDialog(false)
		showCompanyMenu()
	}, [showCompanyMenu])
	const closeCompanyMenu = (
		event: React.MouseEvent | React.ChangeEvent<any>,
	) => {
		if (companyMenuRef && companyMenuRef.current) {
			if (companyMenuRef.current.contains(event.target)) return null
		}
		setIsMenuVisible(false)
	}

	return !activeCompany ? null : (
		<div className={classes.headerContainer}>
			<>
				<IconButton className={classes.closeButton} onClick={goBack}>
					<CloseIcon />
				</IconButton>
				<div className={classes.titleSection}>
					<div className={classes.headerTitle}>
						{t('Create a new Job opening!')}
					</div>
					<div
						className={classes.companyJobContainer}
						onClick={handleOpenDialog}
						ref={companyMenuRef}
					>
						<EntityLogo
							type={EEntity.company}
							extraAvatarClasses={classes.companyLogo}
							fullView={false}
							company={activeCompany}
						/>
						{!isSmDown && (
							<div className={classes.postAsSection}>
								<span className={classes.postAsText}>
									{t('Post as')}
								</span>
								{activeCompany && (
									<span>{activeCompany.name}</span>
								)}
							</div>
						)}
						<KeyboardArrowDown />
					</div>

					{!isSmDown && (
						<Popper
							className={classes.popper}
							open={isMenuVisible}
							placement="bottom-end"
							anchorEl={
								(companyMenuRef && companyMenuRef.current) ||
								null
							}
							disablePortal={true}
							modifiers={{
								flip: {
									enabled: true,
								},
								preventOverflow: {
									enabled: true,
								},
								arrow: {
									enabled:
										!!arrowProfileRef &&
										arrowProfileRef.current,
									element:
										arrowProfileRef &&
										arrowProfileRef.current,
								},
							}}
						>
							<span
								className={classes.arrow}
								ref={arrowProfileRef}
							/>
							<Paper elevation={2}>
								<ClickAwayListener
									onClickAway={closeCompanyMenu}
								>
									<div>
										{companies &&
											companies?.map(
												(item: any, index: number) => {
													return (
														<MenuItem
															onClick={() =>
																handleChoseCompanyJob(
																	item,
																)
															}
															className={
																activeCompany.slug ===
																item.slug
																	? classes.activeJobCompany
																	: classes.jobCompany
															}
															key={`company_${index}`}
															value={item}
														>
															{item.name}
														</MenuItem>
													)
												},
											)}
									</div>
								</ClickAwayListener>
							</Paper>
						</Popper>
					)}
					{isSmDown && (
						<SwipeableDrawer
							anchor="right"
							open={isMenuVisible}
							onOpen={showCompanyMenu}
							onClose={closeCompanyMenu}
							classes={{
								paper: classes.sidebarDrawer,
							}}
						>
							<div className={classes.sidebarHeader}>
								{t('Post as')}
							</div>
							<div style={{ margin: '16px -16px' }}>
								<Divider light />
							</div>
							<div>
								{companies &&
									companies?.map(
										(item: any, index: number) => {
											return (
												<MenuItem
													onClick={() =>
														handleChoseCompanyJob(
															item,
														)
													}
													className={
														activeCompany.slug ===
														item.slug
															? classes.activeJobCompany
															: classes.jobCompany
													}
													key={`company_${index}`}
													value={item}
												>
													{item.name}
												</MenuItem>
											)
										},
									)}
							</div>
						</SwipeableDrawer>
					)}
				</div>
				<Dialog
					open={openDialog}
					onClose={handleCloseDialog}
					aria-labelledby="alert-dialog-title"
					aria-describedby="alert-dialog-description"
				>
					<DialogTitle id="alert-dialog-title">
						{t('Are you sure you want to change the company?')}
					</DialogTitle>
					<DialogContent>
						<DialogContentText id="alert-dialog-description">
							{t(
								`Changing the company will lead to clearing the 'Add location' and 'Team' fields.`,
							)}
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={handleCloseDialog}
							color="primary"
							autoFocus
						>
							{t('Ok')}
						</Button>
					</DialogActions>
				</Dialog>
			</>
		</div>
	)
}

const Header = memo((props: any) => {
	return (
		<StoreValue keys={['job.team|team', 'job.locations|locations']}>
			<HeaderView {...props} />
		</StoreValue>
	)
})
export default Header
